import { useState } from "react";
import { Plus, Pencil, Trash2, Merge, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLoad, act } from "@/lib/hooks";
import { api, fmt } from "@/lib/api";
import { useAccess } from "@/context/AuthContext";
import { Section, EmptyRow, Field, NativeSelect } from "@/components/common";
import { MergeDialog } from "./MergeDialog";

function LocationDialog({ loc, branches, onClose, onDone }) {
  const [f, setF] = useState({ name: loc?.name || "", branch_id: loc?.branch_id || branches[0]?.id || "" });
  const save = async () => {
    const r = await act(() => loc ? api.put(`/inventory/warehouses/${loc.id}`, { name: f.name }) : api.post("/inventory/warehouses", f), loc ? `Renamed to ${f.name}` : `${f.name} added`);
    if (r) { onDone(); onClose(); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="location-dialog">
        <DialogHeader><DialogTitle>{loc ? "Rename storage location" : "Add storage location"}</DialogTitle><DialogDescription>{loc ? "Past movements will show the new name." : "A warehouse, store or shelf area where you keep stock."}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><Input data-testid="location-name-input" value={f.name} autoFocus onChange={(e) => setF({ ...f, name: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter" && f.name.trim()) save(); }} /></Field>
          {!loc && <Field label="Branch"><NativeSelect testId="location-branch-select" value={f.branch_id} onChange={(v) => setF({ ...f, branch_id: v })} options={branches.map((b) => ({ value: b.id, label: b.name }))} /></Field>}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="location-save-button" disabled={!f.name.trim()} onClick={save}>{loc ? "Save name" : "Add location"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ w, admin, onEdit, onRemove, onMerge }) {
  const used = [w.open_jobs.length && `${w.open_jobs.length} open job${w.open_jobs.length > 1 ? "s" : ""}`, w.open_pos.length && `${w.open_pos.length} open PO${w.open_pos.length > 1 ? "s" : ""}`,
    w.pending_adjustments && `${w.pending_adjustments} pending adjustment${w.pending_adjustments > 1 ? "s" : ""}`].filter(Boolean).join(" · ");
  return (
    <tr data-testid={`location-row-${w.id}`}>
      <td className="font-medium" data-testid={`location-name-${w.id}`}>{w.label}{w.same_name.length > 0 && <span className="po-chip po-tone-warning ml-2 !normal-case" data-testid={`location-duplicate-${w.id}`}><AlertTriangle className="mr-1 inline h-3 w-3" />Same name as {w.same_name.length} other</span>}</td>
      <td className="whitespace-nowrap">{w.branch_name}</td>
      <td className="po-num" data-testid={`location-items-${w.id}`}>{w.items_held}</td>
      <td className="po-num" data-testid={`location-value-${w.id}`}>{fmt(w.stock_value)}</td>
      <td className="text-xs text-slate-500">{used || "—"}</td>
      <td className="whitespace-nowrap text-right">{admin && <>
        <Button size="sm" variant="ghost" data-testid={`location-rename-${w.id}`} onClick={() => onEdit(w)}><Pencil className="h-3.5 w-3.5" /></Button>
        {w.same_name.length > 0 && <Button size="sm" variant="outline" data-testid={`location-merge-${w.id}`} onClick={() => onMerge(w)}><Merge className="mr-1 h-3.5 w-3.5" />Merge into…</Button>}
        <Button size="sm" variant="ghost" data-testid={`location-remove-${w.id}`} onClick={() => onRemove(w)} className="hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button></>}</td>
    </tr>
  );
}

export function Locations({ onChanged }) {
  const [whs, reload] = useLoad("/inventory/warehouses");
  const [branches] = useLoad("/tenant/branches");
  const [edit, setEdit] = useState(null);
  const [merge, setMerge] = useState(null);
  const { canWrite } = useAccess();
  if (!whs || !branches) return null;
  const admin = canWrite(["tenant_admin"]);
  const active = (branches.branches || branches).filter((b) => b.status === "active");
  const done = () => { reload(); onChanged(); };
  const remove = async (w) => {
    if (!window.confirm(`Remove storage location "${w.label}"? Past movements will keep showing its name.`)) return;
    if (await act(() => api.delete(`/inventory/warehouses/${w.id}`), `${w.label} removed`)) done();
  };
  return (
    <Section>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <p className="text-sm text-slate-500">Warehouses and stores where you keep stock. A location can be removed only when it is empty and nothing open uses it.</p>
        {admin && <Button size="sm" data-testid="add-location-button" onClick={() => setEdit({})}><Plus className="mr-1 h-4 w-4" />Location</Button>}
      </div>
      <div className="overflow-x-auto"><table className="po-table" data-testid="locations-table">
        <thead><tr><th>Name</th><th>Branch</th><th className="po-num">Items held</th><th className="po-num">Stock value</th><th>In use by</th><th /></tr></thead>
        <tbody>{whs.length === 0 && <EmptyRow cols={6} />}{whs.map((w) => <Row key={w.id} w={w} admin={admin} onEdit={setEdit} onRemove={remove} onMerge={setMerge} />)}</tbody>
      </table></div>
      {edit && <LocationDialog loc={edit.id ? edit : null} branches={active} onClose={() => setEdit(null)} onDone={done} />}
      {merge && <MergeDialog loc={merge} whs={whs} onClose={() => setMerge(null)} onDone={done} />}
    </Section>
  );
}
